import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { canAccess } from '../../utils/rbac';
import { formatMoney, formatDate } from '../../utils/format';
import { fmtDateTime } from '../../utils/dates.js';
import Tag from '../shared/Tag';
import MyDayPanel from './MyDayPanel';
import MySpaceStrip from './MySpaceStrip';
import MyTimePanel from './MyTimePanel';
import { MyRequestsCard, WeekAheadCard, TeamTodayCard } from './HomeCards';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import { NewActionModal } from '../people/MyActionsPage';
import OvertimeActionDecision from '../people/OvertimeActionDecision';
import { SnoozeButton, setSnooze, snoozeDateLabel } from '../people/ActionSnooze';
import { CardFooter, HEADER_LINK, SECTION_HEADING } from '../shared/CardParts';

// ── Helpers ────────────────────────────────────────────────────────────────

function OverdueBadge({ date, status }) {
  if (!date || status === 'done' || status === 'Paid' || status === 'Cancelled' || status === 'completed') return null;
  const today = new Date().toISOString().split('T')[0];
  if (date < today) return <span className="text-[9px] bg-red-100 text-red-700 font-bold px-1 py-0.5 rounded ml-1 whitespace-nowrap">OVERDUE</span>;
  const daysLeft = Math.ceil((new Date(date) - new Date()) / 86400000);
  if (daysLeft <= 7) return <span className="text-[9px] bg-amber-100 text-amber-700 font-bold px-1 py-0.5 rounded ml-1 whitespace-nowrap">DUE SOON</span>;
  return null;
}

function statusLabel(s) {
  const map = { not_started: 'Not Started', in_progress: 'In Progress', done: 'Done',
    completed: 'Completed', on_hold: 'On Hold', cancelled: 'Cancelled' };
  return map[s] || s;
}

function statusColor(s) {
  if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
  if (s === 'done' || s === 'completed') return 'bg-green-100 text-green-700';
  if (s === 'on_hold') return 'bg-amber-100 text-amber-700';
  if (s === 'cancelled') return 'bg-gray-100 text-gray-500';
  return 'bg-gray-100 text-gray-600';
}

// ── Engineer Dashboard ─────────────────────────────────────────────────────

// TaskCard and SectionHeader defined OUTSIDE EngineerDashboard so they are
// stable component references — no unmount/remount on parent re-render.

function TaskCard({ task, isSelected, onSelect, onDragStart, showDrag = true }) {
  return (
    <div
      className={[
        'flex items-start gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors',
        isSelected ? 'border-navy/40 bg-navy/5' : 'border-gray-200 bg-white hover:border-navy/20 hover:bg-gray-50/60',
        task.is_overdue ? 'border-l-2 border-l-red-400' : '',
      ].join(' ')}
      draggable={showDrag}
      onDragStart={showDrag ? e => onDragStart(e, task) : undefined}
      onClick={() => onSelect(task)}
    >
      {showDrag && (
        <div className="mt-0.5 text-gray-300 cursor-grab select-none shrink-0" style={{ fontSize: 11 }}>⠿</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-semibold text-gray-800 truncate">{task.name}</span>
          <OverdueBadge date={task.end_date} status={task.status} />
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {task.sow_reference && (
            <span className="text-[8px] bg-navy/10 text-navy font-semibold px-1 rounded">{task.sow_reference}</span>
          )}
          {task.project_name && !task.sow_reference && (
            <span className="text-[8px] text-gray-400 truncate">{task.project_name}</span>
          )}
          {task.owner_name && (
            <span className="text-[8px] text-gray-400 italic truncate">👤 {task.owner_name}</span>
          )}
          {task.end_date && (
            <span className="text-[8px] text-gray-400">{formatDate(task.end_date)}</span>
          )}
        </div>
      </div>
      <span className={`shrink-0 text-[7px] font-bold px-1.5 py-0.5 rounded uppercase ${statusColor(task.status)}`}>
        {statusLabel(task.status)}
      </span>
    </div>
  );
}

function SectionHeader({ title, count, accent, children }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <h3 className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">{title}</h3>
        {count != null && (
          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${accent || 'bg-gray-100 text-gray-500'}`}>{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function EngineerDashboard({ currentUser }) {
  const { navigate } = useApp();
  const today = new Date().toISOString().split('T')[0];
  const in2Weeks = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0];
  const in4Weeks = new Date(Date.now() + 28 * 86400000).toISOString().split('T')[0];

  const [tasks, setTasks]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [upcomingWindow, setUpcoming] = useState(2);    // 2 or 4 weeks (only used for 'mine' scope)
  // ── upcoming scope: 'mine' | 'team_unallocated' | 'team_all' ──
  const [upcomingScope, setUpcomingScope] = useState('mine');
  const [teamTasks, setTeamTasks]         = useState([]);
  const [teamTasksLoading, setTeamTasksLoading] = useState(false);

  const [selectedTask, setSelected]   = useState(null);
  const [dragOver, setDragOver]       = useState(false);
  const draggingRef                   = useRef(null);

  // Detail panel state — all in the parent (not in inner component) to avoid
  // losing focus: the textarea must not be inside a component that remounts.
  const [comments, setComments]       = useState([]);
  const [commentsLoading, setCL]      = useState(false);
  const [newComment, setNewComment]   = useState('');
  const [saving, setSaving]           = useState(false);
  const [editStatus, setEditStatus]   = useState('');
  const [editOwner, setEditOwner]     = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [people, setPeople]           = useState([]);
  const [showSendTask, setShowSendTask] = useState(false);
  const [sows, setSows] = useState([]);

  const reload = useCallback(() => {
    setLoading(true);
    api.get('tasks/mine')
      .then(t => { setTasks(t); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);
  // Silent re-fetch when returning to the tab (no loading flash)
  useRefreshOnFocus(() => { api.get('tasks/mine').then(t => setTasks(t)).catch(() => {}); });

  // Load people list for owner reassign and SOWs for Send Task form
  useEffect(() => {
    api.get('people').then(p => setPeople((p || []).filter(person => person.employee_status !== 'archived'))).catch(() => {});
    api.get('sows', { status: 'Active' }).then(s => setSows(s || [])).catch(() => {});
  }, []);

  // Load team tasks whenever scope changes (non-mine)
  useEffect(() => {
    if (upcomingScope === 'mine') return;
    setTeamTasksLoading(true);
    api.get('tasks/mine', { scope: upcomingScope })
      .then(t => { setTeamTasks(t); setTeamTasksLoading(false); })
      .catch(() => { setTeamTasks([]); setTeamTasksLoading(false); });
  }, [upcomingScope]);

  // Load comments when a task is selected
  useEffect(() => {
    if (!selectedTask) return;
    setCL(true);
    setEditStatus(selectedTask.status);
    setEditOwner(selectedTask.owner_id || '');
    setEditDueDate(selectedTask.end_date || '');
    api.get('commentary', { entity_type: 'task', entity_id: selectedTask.id })
      .then(c => { setComments(c); setCL(false); })
      .catch(() => setCL(false));
  }, [selectedTask?.id]);

  // Call this after any action that auto-posts a commentary note
  function reloadComments(taskId) {
    setCL(true);
    api.get('commentary', { entity_type: 'task', entity_id: taskId })
      .then(c => { setComments(c); setCL(false); })
      .catch(() => setCL(false));
  }

  // Buckets (only from personal tasks)
  const windowDate = upcomingWindow === 2 ? in2Weeks : in4Weeks;
  const working    = tasks.filter(t => t.status === 'in_progress');
  const overdue    = tasks.filter(t => t.is_overdue && !['in_progress', 'done', 'completed', 'cancelled', 'n_a'].includes(t.status));
  const myUpcoming = tasks.filter(t =>
    !t.is_overdue &&
    t.status !== 'in_progress' &&
    t.end_date &&
    t.end_date >= today &&
    t.end_date <= windowDate
  );
  const noDate = tasks.filter(t =>
    !t.is_overdue && t.status !== 'in_progress' && (!t.end_date || t.end_date > windowDate)
  );

  // What to show in the Upcoming panel depends on scope
  const upcomingList = upcomingScope === 'mine' ? myUpcoming : teamTasks;

  // ── API helpers ──────────────────────────────────────────────────────────

  const STATUS_LABELS = {
    not_started: 'Not Started', in_progress: 'In Progress',
    completed: 'Completed', blocked: 'Blocked', cancelled: 'Cancelled', n_a: 'N/A',
  };


  async function updateTaskStatus(task, newStatus) {
    setSaving(true);
    try {
      if (task.task_source === 'sow_task') {
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, { status: newStatus });
      } else {
        await api.put(`tasks/${task.id}`, { status: newStatus });
      }
      const terminal = ['done', 'completed', 'cancelled', 'n_a'].includes(newStatus);
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus, ...(terminal ? { is_overdue: false } : {}) } : t));
      if (selectedTask?.id === task.id) setSelected(s => ({ ...s, status: newStatus, ...(terminal ? { is_overdue: false } : {}) }));

      // Log the status change as a journey note
      const actorName = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : 'Unknown';
      const fromLabel = STATUS_LABELS[task.status] || task.status;
      const toLabel   = STATUS_LABELS[newStatus]   || newStatus;
      await api.post('commentary', {
        entity_type: 'task',
        entity_id: task.id,
        content: `📋 Status changed from ${fromLabel} → ${toLabel} by ${actorName}.`,
      });
      reloadComments(task.id);
    } finally {
      setSaving(false);
    }
  }

  async function updateTaskOwner(task, newOwnerId) {
    // Send null (not empty string) to properly clear the DB field
    const ownerIdToSend = newOwnerId || null;
    setSaving(true);
    try {
      if (task.task_source === 'sow_task') {
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, { owner_id: ownerIdToSend });
      } else {
        await api.put(`tasks/${task.id}`, { assignee_id: ownerIdToSend });
      }
      const person = people.find(p => p.id === ownerIdToSend);
      const name   = person ? `${person.first_name} ${person.last_name}` : '';
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, owner_id: ownerIdToSend, owner_name: name } : t));
      if (selectedTask?.id === task.id) setSelected(s => ({ ...s, owner_id: ownerIdToSend, owner_name: name }));
    } finally {
      setSaving(false);
    }
  }

  async function updateTaskDueDate(task, newDate) {
    setSaving(true);
    try {
      if (task.task_source === 'sow_task') {
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, { start_date: newDate || null });
      } else {
        await api.put(`tasks/${task.id}`, { end_date: newDate || null });
      }
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, end_date: newDate || null } : t));
      if (selectedTask?.id === task.id) setSelected(s => ({ ...s, end_date: newDate || null }));
      const actorName = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : 'Unknown';
      await api.post('commentary', {
        entity_type: 'task',
        entity_id: task.id,
        content: newDate
          ? `📅 Due date set to ${newDate} by ${actorName}.`
          : `📅 Due date cleared by ${actorName}.`,
      });
      reloadComments(task.id);
    } finally {
      setSaving(false);
    }
  }

  // Return a task back to the team pool (clear owner, reset status, log journey note)
  async function returnToTeam(task) {
    setSaving(true);
    try {
      const returnerName = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : 'Unknown';

      const journey = {
        entity_type: 'task',
        entity_id: task.id,
        content: `↩ Returned to team pool by ${returnerName}. Status reset to Not Started.`,
      };
      if (task.task_source === 'sow_task') {
        // 1. Clear owner, 2. reset status to not_started, 3. journey note.
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, { owner_id: null });
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, { status: 'not_started' });
        await api.post('commentary', journey);
      } else {
        // A standalone task and its notes are its assignee's (or Task
        // Oversight's), so the note goes first and the owner and status
        // change in one request: once the owner is cleared, nothing after it
        // would still be the assignee's. The note is kept on screen, because
        // the thread is no longer this person's to reload.
        const note = await api.post('commentary', journey);
        await api.put(`tasks/${task.id}`, { assignee_id: null, status: 'not_started' });
        if (note?.id) setComments(prev => [note, ...(prev || [])]);
      }
    } finally {
      setSaving(false);
    }

    // Keep the task detail panel open so the user can still read the task or add a note.
    // Update local state to reflect the returned status (no owner, reset to not_started).
    setSelected(prev => prev ? { ...prev, owner_id: null, assignee_id: null, owner_name: null, status: 'not_started' } : null);
    setEditOwner('');
    setEditStatus('not_started');
    if (task.task_source === 'sow_task') reloadComments(task.id);
    reload(); // flush task from working / upcoming 'mine' lists immediately
    // Also refresh the team scope list if it's visible
    if (upcomingScope !== 'mine') {
      setTeamTasksLoading(true);
      api.get('tasks/mine', { scope: upcomingScope })
        .then(t => { setTeamTasks(t); setTeamTasksLoading(false); })
        .catch(() => setTeamTasksLoading(false));
    }
  }

  // Claim an unallocated team task for yourself
  async function claimTask(task) {
    setSaving(true);
    try {
      const claimerName = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : 'Unknown';

      if (task.task_source === 'sow_task') {
        await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, { owner_id: currentUser?.id });
      } else {
        await api.put(`tasks/${task.id}`, { assignee_id: currentUser?.id });
      }

      // Log journey note
      await api.post('commentary', {
        entity_type: 'task',
        entity_id: task.id,
        content: `✋ Claimed by ${claimerName} from team pool.`,
      });
    } finally {
      setSaving(false);
    }

    reload(); // brings it into the 'mine' list
    reloadComments(task.id); // show the auto-posted "Claimed" note in the detail panel
    // Refresh the team scope too
    setTeamTasksLoading(true);
    api.get('tasks/mine', { scope: upcomingScope })
      .then(t => { setTeamTasks(t); setTeamTasksLoading(false); })
      .catch(() => setTeamTasksLoading(false));
  }

  async function postComment() {
    if (!newComment.trim() || !selectedTask) return;
    setSaving(true);
    try {
      const c = await api.post('commentary', {
        entity_type: 'task', entity_id: selectedTask.id, content: newComment.trim()
      });
      setComments(prev => [c, ...prev]);
      setNewComment('');
    } finally {
      setSaving(false);
    }
  }

  // ── Drag-and-drop ────────────────────────────────────────────────────────

  function onTaskDragStart(e, task) {
    draggingRef.current = task;
    e.dataTransfer.effectAllowed = 'move';
  }

  async function onDropWorking(e) {
    e.preventDefault();
    setDragOver(false);
    const task = draggingRef.current;
    draggingRef.current = null;
    if (!task || task.status === 'in_progress') return;

    // Check both owner_id (sow_tasks) and assignee_id (standalone tasks)
    const currentOwnerId = task.owner_id || task.assignee_id;
    const shouldClaim = currentUser?.id && currentOwnerId !== currentUser.id;
    const actorName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}`.trim() : 'Unknown';

    try {
      if (shouldClaim) {
        // Set both status + owner in one API call to avoid partial updates
        if (task.task_source === 'sow_task') {
          await api.put(`sows/${task.sow_id}/project-tasks/${task.id}`, { status: 'in_progress', owner_id: currentUser.id });
        } else {
          await api.put(`tasks/${task.id}`, { status: 'in_progress', assignee_id: currentUser.id });
        }
        const ownerName = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ');
        const claimedTask = { ...task, status: 'in_progress', owner_id: currentUser.id, assignee_id: currentUser.id, owner_name: ownerName };
        // Add to personal tasks (may be a team task not yet in the 'mine' list)
        setTasks(prev => {
          const exists = prev.some(t => t.id === task.id);
          return exists
            ? prev.map(t => t.id === task.id ? claimedTask : t)
            : [...prev, claimedTask];
        });
        // Remove from team tasks list so it disappears from Upcoming immediately
        setTeamTasks(prev => prev.filter(t => t.id !== task.id));
        if (selectedTask?.id === task.id) {
          setSelected(s => ({ ...s, status: 'in_progress', owner_id: currentUser.id, owner_name: ownerName }));
        }
        await api.post('commentary', {
          entity_type: 'task',
          entity_id: task.id,
          content: `✋ Claimed and started by ${actorName}.`,
        });
        reloadComments(task.id);
      } else {
        // Already owned — just update status
        await updateTaskStatus(task, 'in_progress');
      }
    } catch (err) {
      console.error('onDropWorking failed:', err);
    }
    // Silent background reconcile — catches any case where optimistic update
    // didn't fully reflect (e.g. task was only in teamTasks, not tasks)
    api.get('tasks/mine').then(t => setTasks(t)).catch(() => {});
  }

  // ── Render ───────────────────────────────────────────────────────────────

  // Detail panel is rendered INLINE (not as <DetailPanel/>) so React never
  // sees it as a new component type on re-renders — this is what keeps the
  // textarea focused while typing. Defining it as an inner JSX block avoids
  // the unmount/remount cycle that inner-component-as-JSX-element causes.
  const detailPanelJSX = selectedTask ? (() => {
    const t = selectedTask;
    const isDone = t.status === 'done' || t.status === 'completed';
    const isTeamTask = upcomingScope !== 'mine' && !working.concat(overdue, myUpcoming, noDate).find(x => x.id === t.id);
    const statusOptions = t.task_source === 'sow_task'
      ? ['not_started', 'in_progress', 'completed', 'on_hold', 'cancelled']
      : ['not_started', 'in_progress', 'done', 'on_hold', 'cancelled'];

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col" style={{ minHeight: 0, maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }}>
        {/* Header */}
        <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold text-gray-800 leading-snug">{t.name}</div>
            {t.sow_reference && (
              <div className="text-[8px] text-navy font-semibold mt-0.5">{t.sow_reference}</div>
            )}
            {t.project_name && !t.sow_reference && (
              <div className="text-[8px] text-gray-400 mt-0.5">{t.project_name}</div>
            )}
          </div>
          <button
            className="text-gray-300 hover:text-gray-500 transition-colors shrink-0"
            onClick={() => setSelected(null)}
            style={{ fontSize: 16, lineHeight: 1 }}
          >×</button>
        </div>

        {/* Metadata + actions */}
        <div className="px-4 py-3 border-b border-gray-100 space-y-2">
          {/* Status */}
          <div className="flex items-center gap-2">
            <span className="text-[8px] uppercase font-bold text-gray-400 w-14 shrink-0">Status</span>
            <select
              className="text-[9px] border border-gray-200 rounded px-1 py-0.5 flex-1"
              value={editStatus}
              disabled={saving}
              onChange={async e => {
                const val = e.target.value;
                setEditStatus(val);
                await updateTaskStatus(t, val);
              }}
            >
              {statusOptions.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
          </div>
          {/* Owner */}
          <div className="flex items-center gap-2">
            <span className="text-[8px] uppercase font-bold text-gray-400 w-14 shrink-0">Owner</span>
            <select
              className="text-[9px] border border-gray-200 rounded px-1 py-0.5 flex-1"
              value={editOwner}
              disabled={saving}
              onChange={async e => {
                const val = e.target.value;
                setEditOwner(val);
                await updateTaskOwner(t, val);
              }}
            >
              <option value="">— Unassigned (team pool) —</option>
              {people.map(p => (
                <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
              ))}
            </select>
          </div>
          {/* Due date — editable */}
          <div className="flex items-center gap-2">
            <span className="text-[8px] uppercase font-bold text-gray-400 w-14 shrink-0">Due</span>
            <input
              type="date"
              className={`text-[9px] border rounded px-1 py-0.5 flex-1 ${editDueDate && t.is_overdue ? 'border-red-300 text-red-600' : 'border-gray-200 text-gray-700'}`}
              value={editDueDate}
              disabled={saving}
              onChange={async e => {
                const val = e.target.value;
                setEditDueDate(val);
                await updateTaskDueDate(t, val);
              }}
            />
            {editDueDate && (
              <button
                className="text-[8px] text-gray-300 hover:text-red-400 transition-colors"
                disabled={saving}
                title="Clear due date"
                onClick={async () => { setEditDueDate(''); await updateTaskDueDate(t, ''); }}
              >×</button>
            )}
          </div>
          {/* Description */}
          {t.description && (
            <div className="text-[9px] text-gray-500 bg-gray-50 rounded p-2 leading-relaxed">{t.description}</div>
          )}
          {/* Quick action buttons */}
          <div className="flex gap-1.5 pt-1 flex-wrap">
            {!isDone && (
              <button
                className="text-[8px] bg-green-600 hover:bg-green-700 text-white font-semibold px-2 py-1 rounded transition-colors"
                disabled={saving}
                onClick={() => updateTaskStatus(t, t.task_source === 'sow_task' ? 'completed' : 'done')}
              >✓ Mark Done</button>
            )}
            {t.status !== 'in_progress' && !isDone && (
              <button
                className="text-[8px] bg-blue-600 hover:bg-blue-700 text-white font-semibold px-2 py-1 rounded transition-colors"
                disabled={saving}
                onClick={() => updateTaskStatus(t, 'in_progress')}
              >▶ Start</button>
            )}
            {t.status !== 'on_hold' && !isDone && (
              <button
                className="text-[8px] bg-amber-500 hover:bg-amber-600 text-white font-semibold px-2 py-1 rounded transition-colors"
                disabled={saving}
                onClick={() => updateTaskStatus(t, 'on_hold')}
              >⏸ Hold</button>
            )}
            {isDone && (
              <button
                className="text-[8px] bg-gray-500 hover:bg-gray-600 text-white font-semibold px-2 py-1 rounded transition-colors"
                disabled={saving}
                onClick={() => updateTaskStatus(t, 'in_progress')}
              >↩ Reopen</button>
            )}
            {/* Claim: if viewing team tasks and it's unassigned or assigned to someone else */}
            {!isDone && t.owner_id !== currentUser?.id && (
              <button
                className="text-[8px] bg-navy hover:bg-navy/80 text-white font-semibold px-2 py-1 rounded transition-colors"
                disabled={saving}
                title="Assign this task to yourself"
                onClick={() => claimTask(t)}
              >+ Claim</button>
            )}
            {/* Return to team: if it's currently assigned to the current user */}
            {t.owner_id === currentUser?.id && !isDone && (
              <button
                className="text-[8px] bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold px-2 py-1 rounded transition-colors"
                disabled={saving}
                title="Remove yourself as owner — returns this task to the team pool"
                onClick={() => returnToTeam(t)}
              >↩ Return to Team</button>
            )}
          </div>
        </div>

        {/* Activity & Notes — textarea lives here, NOT in a child component
            so it is never unmounted while typing */}
        <div className="flex flex-col px-4 py-3" style={{ minHeight: 0 }}>
          <div className="text-[8px] uppercase font-bold text-gray-400 mb-2">Activity &amp; Notes</div>
          <div className="flex gap-2 mb-3">
            <textarea
              className="flex-1 text-[9px] border border-gray-200 rounded px-2 py-1.5 resize-none focus:outline-none focus:border-navy/40"
              rows={2}
              placeholder="Add a note or update… (Ctrl/⌘+Enter to post)"
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }}
            />
            <button
              className="text-[8px] bg-navy text-white font-semibold px-2 py-1 rounded hover:bg-navy/80 transition-colors self-end disabled:opacity-50"
              disabled={saving || !newComment.trim()}
              onClick={postComment}
            >Post</button>
          </div>
          <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 260 }}>
            {commentsLoading && (
              <div className="text-[9px] text-gray-400 text-center py-2">Loading…</div>
            )}
            {!commentsLoading && comments.length === 0 && (
              <div className="text-[9px] text-gray-400 text-center py-2">No notes yet</div>
            )}
            {comments.map(c => (
              <div key={c.id} className="bg-gray-50 rounded p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[8px] font-semibold text-navy">{c.person_name}</span>
                  <span className="text-[7px] text-gray-400">{fmtDateTime(c.created_at)}</span>
                </div>
                <p className="text-[9px] text-gray-700 leading-relaxed whitespace-pre-wrap">{c.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  })() : null;

  const upcomingScopeLabel = {
    mine:             'My tasks',
    team_unallocated: 'Team · Unallocated',
    team_all:         'Team · All',
  };

  return (
    <div className="fade-in dash flex flex-col gap-3" style={{ height: 'calc(100vh - 56px)' }}>

      {/* Banner */}
      <div className="bg-gradient-to-br from-navy to-[#1a3a5c] rounded-lg px-5 py-3 text-white relative overflow-hidden shrink-0">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gold/10 pointer-events-none" />
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xs font-bold text-gold-light">
              Welcome back, {currentUser?.first_name || 'Engineer'} — Alton Valley
            </h3>
            <p className="text-[9px] text-white/50 mt-0.5">
              {tasks.length} open task{tasks.length !== 1 ? 's' : ''} ·{' '}
              {working.length} in progress ·{' '}
              {overdue.length > 0 ? <span className="text-red-300 font-semibold">{overdue.length} overdue</span> : 'none overdue'}
            </p>
          </div>
          <button onClick={() => setShowSendTask(true)}
            className="flex-shrink-0 btn btn-primary text-[10px] px-3 py-1">
            + New Action
          </button>
        </div>
      </div>

      {/* Holiday, next time off and card receipts: the same personal tiles as
          everybody else's home page, kept compact above the task board. */}
      <div className="shrink-0"><MySpaceStrip user={currentUser} onNavigate={navigate} compact /></div>
      <div className="shrink-0"><MyTimePanel onNavigate={navigate} compact /></div>

      {/* My Actions — mention-driven items assigned to or involving me */}
      <MyActionsSection navigate={navigate} />

      {/* New Action modal — the unified person-to-person work item */}
      {showSendTask && (
        <NewActionModal
          people={people} sows={sows} currentUser={currentUser}
          onClose={() => setShowSendTask(false)}
          onCreated={() => { reload(); }}
        />
      )}

      {/* Main grid */}
      <div className="flex flex-col md:flex-row gap-3 flex-1 min-h-0">

        {/* Left column: task lists — hidden on mobile when a task is open */}
        <div className={`flex-col gap-3 flex-1 min-w-0 overflow-y-auto pr-1 ${selectedTask ? 'hidden md:flex' : 'flex'}`}>

          {/* Working On drop zone */}
          <div
            className={[
              'border-2 rounded-lg p-3 transition-colors shrink-0',
              dragOver ? 'border-blue-400 bg-blue-50' : 'border-dashed border-gray-300 bg-white',
            ].join(' ')}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDropWorking}
          >
            <SectionHeader
              title="Working On"
              count={working.length}
              accent="bg-blue-100 text-blue-700"
            >
              <span className="text-[8px] text-gray-400 italic">
                {dragOver ? 'Drop to start working' : 'Drag tasks here to start'}
              </span>
            </SectionHeader>
            {working.length === 0 && !dragOver && (
              <div className="text-center py-3 text-[9px] text-gray-300 italic select-none">
                No tasks in progress — drag one here or click ▶ Start on any task
              </div>
            )}
            <div className="space-y-1.5">
              {working.map(t => (
                <TaskCard key={t.id} task={t} isSelected={selectedTask?.id === t.id}
                  onSelect={setSelected} onDragStart={onTaskDragStart} showDrag={false} />
              ))}
            </div>
          </div>

          {/* Overdue */}
          {overdue.length > 0 && (
            <div className="bg-white border border-red-100 rounded-lg p-3 shrink-0">
              <SectionHeader title="Overdue" count={overdue.length} accent="bg-red-100 text-red-600" />
              <div className="space-y-1.5">
                {overdue.map(t => (
                  <TaskCard key={t.id} task={t} isSelected={selectedTask?.id === t.id}
                    onSelect={setSelected} onDragStart={onTaskDragStart} />
                ))}
              </div>
            </div>
          )}

          {/* Upcoming — with scope toggle */}
          <div className="bg-white border border-gray-200 rounded-lg p-3 shrink-0">
            <SectionHeader
              title="Upcoming"
              count={upcomingScope === 'mine' ? myUpcoming.length : teamTasks.length}
              accent="bg-gray-100 text-gray-600"
            >
              {/* Scope toggle */}
              <div className="flex items-center gap-1 flex-wrap justify-end">
                {(['mine', 'team_unallocated', 'team_all'] ).map(s => (
                  <button
                    key={s}
                    onClick={() => setUpcomingScope(s)}
                    className={[
                      'text-[8px] font-semibold px-2 py-0.5 rounded transition-colors whitespace-nowrap',
                      upcomingScope === s ? 'bg-navy text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                    ].join(' ')}
                  >{upcomingScopeLabel[s]}</button>
                ))}
                {/* Window toggle — only relevant for own tasks */}
                {upcomingScope === 'mine' && (
                  <div className="flex gap-1 ml-1 border-l border-gray-200 pl-1">
                    {[2, 4].map(w => (
                      <button
                        key={w}
                        onClick={() => setUpcoming(w)}
                        className={[
                          'text-[8px] font-semibold px-2 py-0.5 rounded transition-colors',
                          upcomingWindow === w ? 'bg-navy text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                        ].join(' ')}
                      >{w}w</button>
                    ))}
                  </div>
                )}
              </div>
            </SectionHeader>

            {/* Scope description */}
            {upcomingScope !== 'mine' && (
              <div className="text-[8px] text-gray-400 mb-2 italic">
                {upcomingScope === 'team_unallocated'
                  ? 'Tasks assigned to your team with no individual owner — pick one up by clicking + Claim'
                  : 'All active tasks across your team'}
              </div>
            )}

            {(teamTasksLoading && upcomingScope !== 'mine') && (
              <div className="text-center py-3 text-[9px] text-gray-400 italic">Loading…</div>
            )}

            {!teamTasksLoading && upcomingList.length === 0 && (
              <div className="text-center py-3 text-[9px] text-gray-300 italic">
                {upcomingScope === 'mine'
                  ? `No tasks due in the next ${upcomingWindow} weeks`
                  : upcomingScope === 'team_unallocated'
                    ? 'No unallocated tasks in your team right now'
                    : 'No active tasks found for your team'}
              </div>
            )}

            <div className="space-y-1.5">
              {upcomingList.map(t => (
                <TaskCard key={t.id} task={t} isSelected={selectedTask?.id === t.id}
                  onSelect={setSelected} onDragStart={onTaskDragStart} />
              ))}
            </div>
          </div>

          {/* No-date / further out — only for own tasks */}
          {noDate.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3 shrink-0">
              <SectionHeader title="Further Out / No Date" count={noDate.length} />
              <div className="space-y-1.5">
                {noDate.map(t => (
                  <TaskCard key={t.id} task={t} isSelected={selectedTask?.id === t.id}
                    onSelect={setSelected} onDragStart={onTaskDragStart} />
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center py-8 text-[10px] text-gray-400">Loading tasks…</div>
          )}
        </div>

        {/* Right column: Task detail panel */}
        <div className={`overflow-y-auto md:shrink-0 ${selectedTask ? 'flex flex-col' : 'hidden md:flex'}`} style={{ width: '100%', maxWidth: 320 }}>
          {selectedTask
            ? (
              <>
                <button
                  onClick={() => setSelected(null)}
                  className="md:hidden mb-2 text-[9px] text-blue-600 font-semibold self-start hover:underline"
                >
                  ← Back to tasks
                </button>
                {detailPanelJSX}
              </>
            )
            : (
              // Nothing selected: the week ahead and your requests use the
              // column, with the hint kept below them.
              <div className="flex flex-col gap-3 w-full">
                <WeekAheadCard onNavigate={navigate} stacked />
                <MyRequestsCard onNavigate={navigate} />
                <div className="bg-white border border-dashed border-gray-200 rounded-lg flex items-center justify-center flex-1 min-h-[80px] text-center px-4 py-4">
                  <div>
                    <div className="text-xl mb-1">👆</div>
                    <div className="text-[10px] text-gray-400 font-medium">Click any task to view details, add notes, or update its status</div>
                  </div>
                </div>
              </div>
            )
          }
        </div>

      </div>
    </div>
  );
}

// ── My Actions — open actions assigned to or involving the current user.
// On both dashboards: it used to be on the engineer's only, which meant an
// approving manager never saw the queue their overtime approvals land in.
//
// An overtime claim is decided here as well as on the Actions tab, through the
// same panel that tab uses, so the two cannot drift. Every other kind of row
// deep-links to its entity; an overtime row opens the Actions queue, because a
// claim has no screen of its own to open.
// The kind of thing an Action is about, for the line under its title, when it
// has no SOW reference to show. "general" says nothing, so it shows nothing.
const ENTITY_LABEL = { qms_document: 'QMS document', overtime_request: 'Overtime claim', bid: 'Bid', sow: 'SOW' };
function entityLabel(type) {
  if (!type || type === 'general') return '';
  if (ENTITY_LABEL[type]) return ENTITY_LABEL[type];
  const words = String(type).replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function MyActionsSection({ navigate }) {
  const [actions, setActions] = useState(null);
  // Which row's ✕ is awaiting confirmation. Clearing only hides the action from
  // your own list, but it looked like an instant delete — so it asks first.
  const [confirmClear, setConfirmClear] = useState(null);
  // Rows snoozed from here, by action id, so the row says so in place (with
  // Undo) until the next reload drops it from the list.
  const [snoozedHere, setSnoozedHere] = useState({});
  const load = useCallback(() => {
    api.get('actions/mine').then(a => setActions(Array.isArray(a) ? a : [])).catch(() => setActions([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(() => { api.get('actions/mine').then(a => setActions(Array.isArray(a) ? a : [])).catch(() => {}); });

  if (!actions || actions.length === 0) return null;
  const owned = actions.filter(a => a.my_role === 'assignee');
  const undoSnooze = async (id) => {
    try { await setSnooze(id, null); setSnoozedHere(m => { const n = { ...m }; delete n[id]; return n; }); } catch { /* the notice stays */ }
  };
  const clear = async (id) => { setConfirmClear(null); await api.post(`actions/${id}/clear`, { clear: true }).catch(() => {}); load(); };
  const open = (a) => {
    if (!navigate) return;
    if (a.entity_type === 'sow') navigate('sow-detail', a.entity_id, `action-${a.id}`);
    else if (a.entity_type === 'bid') navigate('bid-detail', a.entity_id, `action-${a.id}`);
    // A claim has no screen of its own, so the queue is where the rest of it
    // is: the thread, the history, and the same Approve and Reject.
    else navigate('my-tasks', a.id);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md shrink-0">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-[12px] font-bold text-navy">My Actions <span className="text-gray-400 font-normal">({actions.length})</span></h3>
        <div className="flex items-center gap-2">
          {owned.length > 0 && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{owned.length} waiting on you</span>}
          {navigate && <button onClick={() => navigate('my-tasks')} className={HEADER_LINK}>View all</button>}
        </div>
      </div>
      <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
        {actions.map(a => snoozedHere[a.id] ? (
          <div key={a.id} className="px-3 py-2 text-[11px] text-violet-700 bg-violet-50">
            💤 <span className="font-semibold">{a.title}</span> snoozed until {snoozeDateLabel(snoozedHere[a.id], true)}. Everyone else on it still sees it.{' '}
            <button onClick={() => undoSnooze(a.id)} className="font-bold underline hover:no-underline">Undo</button>
          </div>
        ) : (
          <div key={a.id} className="px-3 py-2 flex items-start gap-2 flex-wrap hover:bg-gray-50">
            <button className="flex-1 min-w-0 text-left" onClick={() => open(a)} title="Open in the SOW">
              {/* The pill sits beside a block holding the title and its subline,
                  so the subline starts under the title rather than the pill. */}
              <div className="flex items-start gap-1.5">
                {a.my_role === 'assignee' && <span className="flex-shrink-0 mt-px text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Waiting on you</span>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[12px] font-semibold text-navy truncate">{a.title}</span>
                    {a.milestone_code && <span className="flex-shrink-0 text-[10px] font-bold text-blue-700 bg-blue-50 px-1 py-0.5 rounded">{a.milestone_code}</span>}
                  </div>
                  {(() => {
                    const meta = [a.sow_reference || entityLabel(a.entity_type), a.last_message && a.last_message.slice(0, 70)].filter(Boolean).join(' · ');
                    return meta ? <div className="text-[11px] text-gray-500 mt-0.5 truncate">{meta}</div> : null;
                  })()}
                </div>
              </div>
            </button>
            {/* Decided here rather than only on the Actions tab. The click is
                stopped inside the panel itself, so deciding does not also
                navigate away from the dashboard. */}
            {a.entity_type === 'overtime_request' && (
              <div className="basis-full min-w-0">
                <OvertimeActionDecision
                  requestId={a.entity_id}
                  canDecide={a.my_role === 'assignee' && !a.my_completed_at}
                  onDecided={load} />
              </div>
            )}
            {confirmClear === a.id ? (
              <div className="flex flex-col items-end gap-1 max-w-[150px] text-right flex-shrink-0">
                <span className="text-[10px] text-gray-600 leading-tight">Remove from your list? It stays open for everyone else. Nothing is deleted.</span>
                <div className="flex gap-1">
                  <button onClick={() => clear(a.id)}
                    className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-700 text-white rounded hover:bg-gray-800">Remove</button>
                  <button onClick={() => setConfirmClear(null)}
                    className="px-1.5 py-0.5 text-[10px] font-semibold border border-gray-200 rounded text-gray-600 hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-1.5 flex-shrink-0">
                <SnoozeButton actionId={a.id} onSnoozed={until => setSnoozedHere(m => ({ ...m, [a.id]: until }))} />
                <button onClick={() => setConfirmClear(a.id)} title="Remove from my list. It stays open for everyone else."
                  className="text-[12px] text-gray-400 hover:text-gray-600 leading-none mt-0.5">✕</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Named export so MyTasksPage can import it directly ──────────────────────
export { EngineerDashboard };

// ── Manager / Default Dashboard ────────────────────────────────────────────

export default function Dashboard() {
  const { currentUser, navigate } = useApp();
  const isEngineer = currentUser?.user_role === 'engineer' ||
    (currentUser?.user_role && currentUser.user_role.split(',').map(r => r.trim()).includes('engineer') &&
     !currentUser.user_role.split(',').map(r => r.trim()).some(r => ['admin', 'pm', 'finance', 'hr'].includes(r)));

  // Engineer gets the purpose-built task view
  if (isEngineer) return <EngineerDashboard currentUser={currentUser} />;

  // ── Manager/admin dashboard ─────────────────────────────────────────────
  //
  // Lists rather than counts. The milestone card used to give three numbers
  // and the projects card one (the same one the header strip already showed),
  // so every question "which ones?" meant leaving the home page. Each card now
  // names the items that need attention and opens the SOW in one click.
  const [sows, setSows]               = useState([]);
  const [myMilestones, setMyMilestones] = useState([]);
  const [myTasks, setMyTasks]         = useState([]);
  const [loading, setLoading]         = useState(true);
  // SOW and milestone values are the sow:see-financials grant; the API leaves
  // them out for anybody else (pen test AD26341, AD12/AD16), so the cards
  // count and name the work without pricing it.
  const seesMoney = canAccess('sow:see-financials');

  const load = useCallback(() => {
    if (!currentUser?.id) return;
    return Promise.all([
      api.get('sows', { status: 'Active', owner_id: currentUser.id }),
      api.get('milestones', { assigned_to_id: currentUser.id, active_only: 1 }),
      api.get('tasks/mine'),
    ]).then(([s, m, t]) => {
      setSows(Array.isArray(s) ? s : []);
      setMyMilestones(Array.isArray(m) ? m : []);
      setMyTasks(Array.isArray(t) ? t : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [currentUser?.id]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);

  const today = new Date().toISOString().split('T')[0];
  const in30  = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  const DONE  = new Set(['Invoiced','Paid','Cancelled','Complete','Completed','Ready to bill','Ready To Bill']);

  const msOverdue  = myMilestones.filter(m => !DONE.has(m.status) && m.forecast_date && m.forecast_date < today);
  const msDue30    = myMilestones.filter(m => !DONE.has(m.status) && m.forecast_date && m.forecast_date >= today && m.forecast_date <= in30);
  const msOnTrack  = myMilestones.filter(m => !DONE.has(m.status) && (!m.forecast_date || m.forecast_date > in30));
  const msVal      = (arr) => arr.reduce((s, m) => s + (m.invoice_amount_excl || 0), 0);
  // Overdue first (oldest first), then the next 30 days in date order. The
  // endpoint already sorts by forecast date, so concatenation keeps the order.
  const msAttention = [...msOverdue, ...msDue30];

  // Overdue tasks first, so the six rows shown are the six that matter most.
  const overdueTasks = myTasks.filter(t => t.is_overdue);
  const tasksShown   = [...overdueTasks, ...myTasks.filter(t => !t.is_overdue)].slice(0, 6);

  const totalValue     = sows.reduce((s, sw) => s + (sw.total_excl_vat || 0), 0);
  const totalInvoiced  = sows.reduce((s, sw) => s + (sw.invoiced_sum || 0), 0);
  const totalRemaining = sows.reduce((s, sw) => s + (sw.remaining_to_invoice || 0), 0);
  // The most left to invoice first: that is where the PM's attention pays.
  const projectsShown  = [...sows].sort((a, b) => (b.remaining_to_invoice || 0) - (a.remaining_to_invoice || 0)).slice(0, 8);

  const businessStats = [
    { label: 'My Active SOWs',    value: sows.length,                accent: 'text-gold-light' },
    ...(seesMoney ? [
      { label: 'Total Value',       value: formatMoney(totalValue, 0), accent: 'text-gold-light' },
      { label: 'Invoiced',          value: formatMoney(totalInvoiced, 0), accent: 'text-gold-light' },
      { label: 'Left to Invoice',   value: formatMoney(totalRemaining, 0), accent: totalRemaining > 0 ? 'text-amber-300' : 'text-gold-light' },
    ] : []),
  ];

  return (
    <div className="fade-in dash">
      {/* The SOW totals only mean something to somebody who runs a SOW. */}
      <MyDayPanel user={currentUser} onNavigate={navigate} businessStats={sows.length ? businessStats : null} />

      {/* Holiday, next time off and card receipts: the things everybody comes
          to the home page for, whatever their role. */}
      <MySpaceStrip user={currentUser} onNavigate={navigate} />

      {/* What is waiting on this person, decidable in place, comes before
          the figures about their time so it is on the first screen. Overtime
          approvals land here, which is why the red chip that used to count
          them and point at another screen has gone. */}
      <div className="mb-6 empty:hidden"><MyActionsSection navigate={navigate} /></div>

      {/* Booked time against what each day should hold, what is not booked
          while it can still be fixed, and a few figures about their time here. */}
      <MyTimePanel onNavigate={navigate} />

      {/* Where you will be this week and what you are waiting on. Each card
          stays away when it has nothing to say, and the other takes the room. */}
      <div className="mb-6 grid grid-cols-1 xl:grid-cols-5 gap-3 xl:items-start empty:hidden">
        <WeekAheadCard onNavigate={navigate} className="xl:col-span-3 xl:only:col-span-5" />
        <MyRequestsCard onNavigate={navigate} className="xl:col-span-2 xl:only:col-span-5" />
      </div>

      {/* A line manager's direct reports: where they are, what needs them. */}
      <TeamTodayCard onNavigate={navigate} />

      {/* Delivery work, for those who have some. Most people own no SOW,
          milestone or plan task, and three empty cards saying so pushed
          nothing useful down the page. Each card shows only when it has rows. */}
      {!loading && (sows.length > 0 || myMilestones.length > 0 || myTasks.length > 0) && (
      <>
      <h2 className={SECTION_HEADING}>My Projects &amp; Delivery</h2>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">

        {/* My Milestones: totals, then the ones that need attention by name */}
        {myMilestones.length > 0 && (
        <div className={`bg-white border border-gray-200 rounded-md flex flex-col ${myTasks.length ? 'xl:col-span-3' : 'xl:col-span-5'}`}>
          <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-[12px] font-bold">My Milestones <span className="text-gray-400 font-normal">({myMilestones.length})</span></h3>
            <button onClick={() => navigate('milestones')} className={HEADER_LINK}>View all</button>
          </div>
          {loading ? (
            <div className="p-4 animate-pulse space-y-2">{[...Array(4)].map((_,i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}</div>
          ) : (
            <>
              <div className="p-3 grid grid-cols-3 gap-2">
                <button onClick={() => navigate('milestones', 'overdue')}
                  className="deep-tint bg-red-50 border border-red-100 rounded-lg p-3 text-left hover:bg-red-100/60 transition-colors">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-red-700 mb-1">Overdue</div>
                  <div className="text-2xl font-extrabold text-red-600 leading-none">{msOverdue.length}</div>
                  {seesMoney && <div className="text-[12px] font-semibold text-red-700 mt-1">{formatMoney(msVal(msOverdue), 0)}</div>}
                </button>
                <button onClick={() => navigate('milestones', 'due30')}
                  className="deep-tint bg-amber-50 border border-amber-100 rounded-lg p-3 text-left hover:bg-amber-100/60 transition-colors">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-1">Due in 30 days</div>
                  <div className="text-2xl font-extrabold text-amber-600 leading-none">{msDue30.length}</div>
                  {seesMoney && <div className="text-[12px] font-semibold text-amber-700 mt-1">{formatMoney(msVal(msDue30), 0)}</div>}
                </button>
                <button onClick={() => navigate('milestones', 'ontrack')}
                  className="deep-tint bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-left hover:bg-emerald-100/60 transition-colors">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 mb-1">On track</div>
                  <div className="text-2xl font-extrabold text-emerald-600 leading-none">{msOnTrack.length}</div>
                  {seesMoney && <div className="text-[12px] font-semibold text-emerald-700 mt-1">{formatMoney(msVal(msOnTrack), 0)}</div>}
                </button>
              </div>
              {msAttention.length === 0 ? (
                <div className="px-3 pb-3 text-[12px] text-emerald-700">Nothing overdue or due in the next 30 days.</div>
              ) : (
                <div className="flex-1 flex flex-col border-t border-gray-100 divide-y divide-gray-100">
                  {msAttention.slice(0, 6).map(m => {
                    const late = m.forecast_date < today;
                    return (
                      <button key={m.id} onClick={() => navigate('sow-detail', m.sow_id)} title="Open the SOW"
                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gold/5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${late ? 'bg-red-500' : 'bg-amber-400'}`} />
                        <span className="text-[11px] font-bold text-navy whitespace-nowrap">{m.reference} · {m.milestone_code}</span>
                        <span className="flex-1 min-w-0 truncate text-[11px] text-gray-600">{m.description || m.client_name || ''}</span>
                        <span className={`text-[11px] whitespace-nowrap ${late ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>{formatDate(m.forecast_date)}</span>
                        {seesMoney && <span className="text-[11px] text-gray-600 whitespace-nowrap w-16 text-right">{formatMoney(m.invoice_amount_excl || 0, 0)}</span>}
                      </button>
                    );
                  })}
                  {msAttention.length > 6 && (
                    <CardFooter className="mt-auto">
                      Showing 6 of {msAttention.length} overdue or due soon
                    </CardFooter>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        )}

        {/* My Tasks */}
        {myTasks.length > 0 && (
        <div className={`bg-white border border-gray-200 rounded-md flex flex-col ${myMilestones.length ? 'xl:col-span-2' : 'xl:col-span-5'}`}>
          <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-[12px] font-bold">My Tasks <span className="text-gray-400 font-normal">({myTasks.length})</span></h3>
            <div className="flex items-center gap-2">
              {overdueTasks.length > 0 && (
                <span className="text-[10px] bg-red-100 text-red-700 font-bold px-1.5 py-0.5 rounded">{overdueTasks.length} overdue</span>
              )}
              <button onClick={() => navigate('project-update')} className={HEADER_LINK}>View all</button>
            </div>
          </div>
          <div className="overflow-x-auto flex-1 flex flex-col">
            <table className="w-full">
              <thead><tr>
                <th className="text-left pl-3 pr-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50">Task</th>
                <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50">Status</th>
              </tr></thead>
              <tbody>
                {tasksShown.map((t) => (
                  <tr key={t.id} className={`hover:bg-gold/5 cursor-pointer ${t.is_overdue ? 'bg-red-50/40' : ''}`}
                    onClick={() => navigate('sow-detail', t.sow_id)}>
                    <td className="pl-3 pr-2 py-1.5">
                      <div className="text-[11px] font-medium text-pretty">{t.name}</div>
                      {/* The due tag rides on the meta line, so a title wraps on its own words. */}
                      <div className="text-[10px] text-gray-500 truncate">
                        Due <span className={t.is_overdue ? 'text-red-700 font-semibold' : 'text-gray-700'}>{formatDate(t.end_date)}</span>
                        <OverdueBadge date={t.end_date} status={t.status} />
                        {t.project_name ? <span className="ml-1">· {t.project_name}</span> : null}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap"><Tag status={t.status} /></td>
                  </tr>
                ))}
                {myTasks.length === 0 && !loading && (
                  <tr><td colSpan={2} className="text-center py-4 text-[11px] text-gray-400">No open tasks assigned to you</td></tr>
                )}
              </tbody>
            </table>
            {myTasks.length > tasksShown.length && (
              <CardFooter className="mt-auto">
                Showing {tasksShown.length} of {myTasks.length}, overdue first
              </CardFooter>
            )}
          </div>
        </div>
        )}

        {/* My Projects: where the money left to invoice sits */}
        {sows.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-md xl:col-span-5">
          <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-[12px] font-bold">My Projects <span className="text-gray-400 font-normal">({sows.length} active)</span></h3>
            <button onClick={() => navigate('my-sows')} className={HEADER_LINK} title="Allocated, upcoming and historical SOWs">View all SOWs</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                <th className="text-left pl-3 pr-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50">SOW</th>
                <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50">Client</th>
                <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50">Detail</th>
                {seesMoney && <>
                <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50">Value</th>
                <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50 w-28">Invoiced</th>
                <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50">Ready to Bill</th>
                <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wide whitespace-nowrap text-gray-500 font-bold border-b bg-gray-50">Left to Invoice</th>
                </>}
              </tr></thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={seesMoney ? 7 : 3} className="p-3"><div className="h-8 bg-gray-100 rounded animate-pulse" /></td></tr>
                )}
                {!loading && projectsShown.map(s => {
                  const total = s.total_excl_vat || 0;
                  const pct = total > 0 ? Math.min(100, Math.round(((s.invoiced_sum || 0) / total) * 100)) : 0;
                  return (
                    <tr key={s.id} className="hover:bg-gold/5 cursor-pointer" onClick={() => navigate('sow-detail', s.id)} title="Open the SOW">
                      <td className="pl-3 pr-2 py-1.5 text-[11px] font-bold text-navy whitespace-nowrap">{s.reference}</td>
                      <td className="px-2 py-1.5 text-[11px] whitespace-nowrap">{s.client_name || '–'}</td>
                      <td className="px-2 py-1.5 text-[11px] text-gray-600 min-w-[14rem]" title={s.detail || ''}><span className="line-clamp-2 text-balance">{s.detail || '–'}</span></td>
                      {seesMoney && <>
                      <td className="px-2 py-1.5 text-[11px] text-right whitespace-nowrap">{formatMoney(total, 0)}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-600 w-7">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-[11px] text-right whitespace-nowrap">
                        {s.ready_to_bill_sum ? <span className="text-emerald-700 font-semibold">{formatMoney(s.ready_to_bill_sum, 0)}</span> : <span className="text-gray-400">–</span>}
                      </td>
                      <td className="px-2 py-1.5 text-[11px] text-right whitespace-nowrap font-semibold">{formatMoney(s.remaining_to_invoice || 0, 0)}</td>
                      </>}
                    </tr>
                  );
                })}
                {!loading && sows.length === 0 && (
                  <tr><td colSpan={seesMoney ? 7 : 3} className="text-center py-4 text-[11px] text-gray-400">No active SOWs where you are PM</td></tr>
                )}
              </tbody>
            </table>
            {sows.length > projectsShown.length && (
              <CardFooter>
                Showing {projectsShown.length} of {sows.length}, most left to invoice first
              </CardFooter>
            )}
          </div>
        </div>
        )}

      </div>
      </>
      )}
    </div>
  );
}
